import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import { DocumentReferenceAttachment } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/AITools/AttachmentArbiter/Attachment';
import { DocumentReferenceContent } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/AITools/AttachmentArbiter/modals/DocumentReferenceModal';
import FocusedSurvey from '@lifeomic/phc-web-toolkit/src/components/Surveys/Components/SubjectResponseViewer/FocusedSurvey';
import type {
  DocumentExtractionV2Invocation,
  DocumentExtractionV2Output,
  DocumentExtractionV2Status,
} from '@lifeomic/react-client';
import {
  useActiveAccountId,
  useActiveProjectId,
  useNotify,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useApiClient } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import {
  getObservationMMMYYYYDate,
  getObservationUnit,
  getObservationValueDisplay,
} from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Measurements/util/fhir';
import {
  DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL,
  withApprovedExtractionExtensions,
} from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Measurements/Graphs/Ranges/ExtractionFeedbackActions';
import { ExtractionConfidenceBadge } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Measurements/Graphs/Ranges/ExtractionConfidenceBadge';
import AuditMasterDetail from '../../AIRules/components/AuditMasterDetail';
import { formatDateTime } from '../../AIRules/utils';
import { memberDetailMessages } from './messages';

const IN_FLIGHT_STATUSES: DocumentExtractionV2Status[] = [
  'scheduled',
  'pending',
  'processing',
  'waiting',
];

const NO_FAILURE_REASON_MESSAGE =
  'No failure reason was recorded for this task.';

const SOURCE_PREVIEW_HEIGHT = 420;

const documentReferenceFhirService = new FhirService('DocumentReference');
const communicationFhirService = new FhirService('Communication');

type ReviewableClinicalResourceType =
  | 'AllergyIntolerance'
  | 'Condition'
  | 'Immunization'
  | 'MedicationStatement'
  | 'Observation'
  | 'Procedure';

const reviewableClinicalResourceTypes = new Set<string>([
  'AllergyIntolerance',
  'Condition',
  'Immunization',
  'MedicationStatement',
  'Observation',
  'Procedure',
]);

const clinicalResourceFhirServices: Record<
  ReviewableClinicalResourceType,
  FhirService<ReviewableClinicalResourceType>
> = {
  AllergyIntolerance: new FhirService('AllergyIntolerance'),
  Condition: new FhirService('Condition'),
  Immunization: new FhirService('Immunization'),
  MedicationStatement: new FhirService('MedicationStatement'),
  Observation: new FhirService('Observation'),
  Procedure: new FhirService('Procedure'),
};

type ExtendedDocumentExtractionV2Output = DocumentExtractionV2Output & {
  source_resource_type?: string | null;
  source_resource_id?: string | null;
};

type ExtendedDocumentExtractionV2Invocation = DocumentExtractionV2Invocation & {
  source_resource_type?: string | null;
  source_resource_id?: string | null;
  output?: ExtendedDocumentExtractionV2Output | null;
};

type SourceResource = {
  resourceType: string;
  id: string;
};

export const isDocumentExtractionV2InFlight = (
  task: DocumentExtractionV2Invocation,
) => IN_FLIGHT_STATUSES.includes(task.status);

export const formatDocumentExtractionV2Status = (
  status: DocumentExtractionV2Status,
) => status.charAt(0).toUpperCase() + status.slice(1);

const getTaskSourceResource = (
  task: DocumentExtractionV2Invocation,
): SourceResource | null => {
  const extendedTask = task as ExtendedDocumentExtractionV2Invocation;
  const sourceResourceType =
    extendedTask.source_resource_type ??
    extendedTask.output?.source_resource_type;
  const sourceResourceId =
    extendedTask.source_resource_id ?? extendedTask.output?.source_resource_id;

  if (sourceResourceType && sourceResourceId) {
    return {
      resourceType: sourceResourceType,
      id: sourceResourceId,
    };
  }

  const sourceDocumentReferenceId =
    task.source_document_reference_id ??
    task.output?.source_document_reference_id;

  if (!sourceDocumentReferenceId) {
    return null;
  }

  return {
    resourceType: 'DocumentReference',
    id: sourceDocumentReferenceId.replace(/^DocumentReference\//, ''),
  };
};

const getSourceLabel = (
  task: DocumentExtractionV2Invocation,
  highlightedSourceDocumentReferenceId: string | undefined,
  thisDocumentLabel: string,
) => {
  const sourceResource = getTaskSourceResource(task);

  if (!sourceResource) {
    return '';
  }

  if (
    sourceResource.resourceType === 'DocumentReference' &&
    sourceResource.id === highlightedSourceDocumentReferenceId
  ) {
    return thisDocumentLabel;
  }

  if (sourceResource.resourceType === 'DocumentReference') {
    return sourceResource.id;
  }

  return `${sourceResource.resourceType}/${sourceResource.id}`;
};

const getFailureReason = (task: DocumentExtractionV2Invocation) => {
  const failureReason = (
    task.output as { failure_reason?: string | null } | null | undefined
  )?.failure_reason?.trim();

  return failureReason || NO_FAILURE_REASON_MESSAGE;
};

type DocumentExtractionV2Resource = (
  | NonNullable<
      NonNullable<DocumentExtractionV2Output['fhir_projections']>[number]
    >
  | NonNullable<
      NonNullable<DocumentExtractionV2Output['fhir_artifacts']>[number]
    >
) & {
  dedupe_outcome?: string | null;
  fact_type?: string | null;
  matched_resource_id?: string | null;
  reason?: string | null;
  role?: string | null;
};

type ReviewableClinicalResourceRef = {
  resourceType: ReviewableClinicalResourceType;
  resourceId: string;
};

type ReviewableClinicalResource = ReviewableClinicalResourceRef & {
  resource: fhir.Resource & {
    id?: string;
    extension?: fhir.Extension[];
    code?: fhir.CodeableConcept;
    vaccineCode?: fhir.CodeableConcept;
    medicationCodeableConcept?: fhir.CodeableConcept;
  };
};

const isReviewableClinicalResourceType = (
  resourceType: string | undefined | null,
): resourceType is ReviewableClinicalResourceType =>
  !!resourceType && reviewableClinicalResourceTypes.has(resourceType);

const getPersistedClinicalResourceRefs = (
  task: DocumentExtractionV2Invocation,
): ReviewableClinicalResourceRef[] => {
  const output = task.output;
  const fhirArtifacts = output?.fhir_artifacts;
  const artifacts = Array.isArray(fhirArtifacts) ? fhirArtifacts : [];
  const refs = artifacts
    .filter(
      artifact =>
        artifact.status === 'persisted' &&
        ['clinical-observation', 'clinical-resource'].includes(
          artifact.role ?? '',
        ) &&
        isReviewableClinicalResourceType(artifact.resource_type) &&
        !!artifact.resource_id,
    )
    .map(artifact => ({
      resourceType: artifact.resource_type as ReviewableClinicalResourceType,
      resourceId: artifact.resource_id!,
    }));

  return Array.from(
    new Map(
      refs.map(ref => [`${ref.resourceType}/${ref.resourceId}`, ref]),
    ).values(),
  );
};

const getResourceLabel = (
  resource: DocumentExtractionV2Resource,
  fallback: string,
) => resource.resource_type ?? fallback;

const getResourceDetails = (resource: DocumentExtractionV2Resource) =>
  [
    resource.status ? `Status: ${resource.status}` : null,
    resource.dedupe_outcome
      ? `Dedupe outcome: ${resource.dedupe_outcome}`
      : null,
    resource.matched_resource_id
      ? `Matched resource ID: ${resource.matched_resource_id}`
      : null,
    resource.fact_type ? `Fact type: ${resource.fact_type}` : null,
    resource.role ? `Role: ${resource.role}` : null,
    resource.reason ? `Reason: ${resource.reason}` : null,
    resource.resource_id ? `Resource ID: ${resource.resource_id}` : null,
  ].filter((detail): detail is string => !!detail);

const getExtractedResourceItems = (task: DocumentExtractionV2Invocation) => {
  const output = task.output;
  const fhirProjections = output?.fhir_projections;
  const projections = Array.isArray(fhirProjections) ? fhirProjections : [];
  const fhirArtifacts = output?.fhir_artifacts;
  const artifacts = Array.isArray(fhirArtifacts)
    ? fhirArtifacts.filter(
        artifact => artifact.role !== 'summary-document-reference',
      )
    : [];

  return [
    ...projections.map((projection, index) => ({
      key: `projection-${index}-${projection.resource_id ?? 'pending'}`,
      label: getResourceLabel(projection, 'FHIR projection'),
      details: getResourceDetails(projection),
    })),
    ...artifacts.map((artifact, index) => ({
      key: `artifact-${index}-${artifact.resource_id ?? 'pending'}`,
      label: getResourceLabel(artifact, 'FHIR artifact'),
      details: getResourceDetails(artifact),
    })),
  ];
};

const getDocumentTitle = (
  document: fhir.DocumentReference | null,
  fallback: string,
) =>
  document?.type?.text ||
  document?.description ||
  document?.content?.[0]?.attachment?.title ||
  document?.id ||
  fallback;

const getCommunicationTitle = (communication: fhir.Communication | null) =>
  communication?.category?.[0]?.text ||
  communication?.reasonCode?.[0]?.text ||
  communication?.id ||
  'Source communication';

const getCommunicationContent = (communication: fhir.Communication | null) =>
  (communication?.payload?.[0] as { contentString?: string } | undefined)
    ?.contentString ||
  communication?.note?.[0]?.text ||
  '';

const getExtractedObservationValueDisplay = (observation: fhir.Observation) =>
  [getObservationValueDisplay(observation), getObservationUnit(observation)]
    .filter(Boolean)
    .join(' ');

const getCodeDisplay = (code: fhir.CodeableConcept | undefined) =>
  code?.text ||
  code?.coding?.find(coding => !!coding.display)?.display ||
  code?.coding?.find(coding => !!coding.code)?.code;

const getClinicalResourceDisplayName = (resource: ReviewableClinicalResource) =>
  getCodeDisplay(resource.resource.code) ||
  getCodeDisplay(resource.resource.vaccineCode) ||
  getCodeDisplay(resource.resource.medicationCodeableConcept) ||
  `${resource.resourceType}/${resource.resourceId}`;

const getClinicalResourceValueDisplay = (
  resource: ReviewableClinicalResource,
) => {
  if (resource.resourceType !== 'Observation') {
    return resource.resourceType;
  }

  return [
    getExtractedObservationValueDisplay(resource.resource as fhir.Observation),
    getObservationMMMYYYYDate(resource.resource as fhir.Observation),
  ]
    .filter(Boolean)
    .join(' · ');
};

const getResourceReviewStatus = (resource: ReviewableClinicalResource) =>
  resource.resource.extension?.find(
    extension =>
      extension.url === DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL,
  )?.valueString;

const DocumentExtractionTaskSourcePreview: React.FC<{
  task: DocumentExtractionV2Invocation;
}> = ({ task }) => {
  const { formatMessage } = useIntl();
  const accountId = useActiveAccountId();
  const serviceAuth = useServiceAuth();
  const sourceResource = getTaskSourceResource(task);
  const [sourceDocument, setSourceDocument] =
    useState<fhir.DocumentReference | null>(null);
  const [sourceCommunication, setSourceCommunication] =
    useState<fhir.Communication | null>(null);
  const [isLoadingSource, setIsLoadingSource] = useState(false);
  const [sourceErrorMessage, setSourceErrorMessage] = useState<string | null>(
    null,
  );
  const sourceResourceType = sourceResource?.resourceType;
  const sourceResourceId = sourceResource?.id;

  useEffect(() => {
    let isCancelled = false;

    const loadSource = async () => {
      setSourceDocument(null);
      setSourceCommunication(null);
      setSourceErrorMessage(null);

      if (
        !sourceResourceType ||
        !sourceResourceId ||
        !accountId ||
        !serviceAuth
      ) {
        return;
      }

      setIsLoadingSource(true);
      try {
        if (sourceResourceType === 'DocumentReference') {
          const response = await documentReferenceFhirService.getById(
            serviceAuth,
          )(accountId, sourceResourceId);
          if (!isCancelled) {
            setSourceDocument(response.data);
          }
        } else if (sourceResourceType === 'Communication') {
          const response = await communicationFhirService.getById(serviceAuth)(
            accountId,
            sourceResourceId,
          );
          if (!isCancelled) {
            setSourceCommunication(response.data);
          }
        }
      } catch {
        if (!isCancelled) {
          setSourceErrorMessage(
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionSourceLoadError,
            ),
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingSource(false);
        }
      }
    };

    void loadSource();

    return () => {
      isCancelled = true;
    };
  }, [
    accountId,
    formatMessage,
    serviceAuth,
    sourceResourceId,
    sourceResourceType,
  ]);

  if (!sourceResource) {
    return (
      <Text>
        {formatMessage(
          memberDetailMessages.memberDocumentsExtractionProvenanceUnknownSourceDocument,
        )}
      </Text>
    );
  }

  if (isLoadingSource) {
    return <Text>Loading source...</Text>;
  }

  if (sourceErrorMessage) {
    return <Text color="negative">{sourceErrorMessage}</Text>;
  }

  if (sourceResource.resourceType === 'Communication') {
    return (
      <Box direction="column" gap={1}>
        <Text>{getCommunicationTitle(sourceCommunication)}</Text>
        <Box
          padding={1}
          style={{
            border: '1px solid var(--chroma-palette-divider)',
            maxHeight: SOURCE_PREVIEW_HEIGHT,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          <Text>{getCommunicationContent(sourceCommunication)}</Text>
        </Box>
      </Box>
    );
  }

  if (sourceResource.resourceType === 'QuestionnaireResponse') {
    return (
      <Box direction="column" gap={1}>
        <Text>{`QuestionnaireResponse/${sourceResource.id}`}</Text>
        <Box
          style={{
            border: '1px solid var(--chroma-palette-divider)',
            height: SOURCE_PREVIEW_HEIGHT,
            overflow: 'auto',
          }}
          data-testid="document-extraction-v2-task-source-questionnaire-response"
        >
          <FocusedSurvey
            responseId={sourceResource.id}
            onSurveyCompletion={() => undefined}
          />
        </Box>
      </Box>
    );
  }

  const attachmentUrl = sourceDocument?.content?.[0]?.attachment?.url;
  const sourceDocumentAttachment = sourceDocument
    ? new DocumentReferenceAttachment(sourceDocument)
    : null;

  return (
    <Box direction="column" gap={1}>
      <Text>
        {getDocumentTitle(
          sourceDocument,
          sourceResource.id ||
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionUnknownDocument,
            ),
        )}
      </Text>
      {sourceDocumentAttachment && attachmentUrl ? (
        <Box
          style={{
            border: '1px solid var(--chroma-palette-divider)',
            height: SOURCE_PREVIEW_HEIGHT,
            overflow: 'hidden',
          }}
          data-testid="document-extraction-v2-task-source-document"
        >
          <DocumentReferenceContent
            attachment={sourceDocumentAttachment}
            reference={sourceDocument!}
            disableDeepLink
            disablePdfNav
          />
        </Box>
      ) : (
        <Text>
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionProvenanceUnknownSourceDocument,
          )}
        </Text>
      )}
    </Box>
  );
};

const DocumentExtractionTaskClinicalResources: React.FC<{
  task: DocumentExtractionV2Invocation;
  subjectId: string;
}> = ({ task, subjectId }) => {
  const { formatMessage } = useIntl();
  const apiClient = useApiClient();
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();
  const serviceAuth = useServiceAuth();
  const [notify] = useNotify();
  const [resources, setResources] = useState<ReviewableClinicalResource[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [decisionInFlight, setDecisionInFlight] = useState<string | null>(null);
  const [rejectingResource, setRejectingResource] =
    useState<ReviewableClinicalResource | null>(null);
  const clinicalResourceRefs = useMemo(
    () => getPersistedClinicalResourceRefs(task),
    [task],
  );
  const clinicalResourceRefsKey = clinicalResourceRefs
    .map(ref => `${ref.resourceType}/${ref.resourceId}`)
    .join('|');

  useEffect(() => {
    let isCancelled = false;

    const loadResources = async () => {
      if (!accountId || !serviceAuth || !clinicalResourceRefs.length) {
        setResources([]);
        setErrorMessage(null);
        return;
      }

      try {
        const refsByType = clinicalResourceRefs.reduce((acc, ref) => {
          acc[ref.resourceType] = [
            ...(acc[ref.resourceType] ?? []),
            ref.resourceId,
          ];
          return acc;
        }, {} as Partial<Record<ReviewableClinicalResourceType, string[]>>);
        const resourceGroups = await Promise.all(
          Object.entries(refsByType).map(
            async ([resourceType, resourceIds]) => {
              const typedResourceType =
                resourceType as ReviewableClinicalResourceType;
              const response = await clinicalResourceFhirServices[
                typedResourceType
              ].getByIds(serviceAuth)(accountId, resourceIds);
              return (
                response.data.entry
                  ?.map(entry => entry.resource)
                  .filter(
                    (
                      resource,
                    ): resource is ReviewableClinicalResource['resource'] =>
                      resource.resourceType === typedResourceType,
                  )
                  .map(resource => ({
                    resource,
                    resourceType: typedResourceType,
                    resourceId: resource.id!,
                  })) ?? []
              );
            },
          ),
        );
        if (!isCancelled) {
          setResources(resourceGroups.flat());
          setErrorMessage(null);
        }
      } catch {
        if (!isCancelled) {
          setResources([]);
          setErrorMessage(
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionObservationsLoadError,
            ),
          );
        }
      }
    };

    void loadResources();

    return () => {
      isCancelled = true;
    };
  }, [
    accountId,
    clinicalResourceRefs,
    clinicalResourceRefsKey,
    formatMessage,
    serviceAuth,
  ]);

  const submitFeedback = useCallback(
    async (
      resource: ReviewableClinicalResource,
      decision: 'approved' | 'rejected',
    ) => {
      if (!projectId) {
        return;
      }

      const inFlightKey = `${decision}:${resource.resourceType}/${resource.resourceId}`;
      setDecisionInFlight(inFlightKey);
      try {
        const response = await apiClient.request(
          'POST /v1/agents-v2/document-extraction-v2/resources/:resource_type/:resource_id/feedback',
          {
            resource_type: resource.resourceType,
            resource_id: resource.resourceId,
            subject_id: subjectId,
            project_id: projectId,
            decision,
            task_id: task.task_id,
          },
        );

        if (decision === 'approved') {
          setResources(current =>
            current.map(currentResource =>
              currentResource.resourceType === resource.resourceType &&
              currentResource.resourceId === resource.resourceId &&
              response.data.approved_by &&
              response.data.approved_at
                ? {
                    ...currentResource,
                    resource: withApprovedExtractionExtensions(
                      currentResource.resource,
                      {
                        approvedBy: response.data.approved_by,
                        approvedAt: response.data.approved_at,
                      },
                    ),
                  }
                : currentResource,
            ),
          );
          notify(
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionProvenanceApproveSuccess,
            ),
            'success',
          );
        } else {
          setResources(current =>
            current.filter(
              currentResource =>
                currentResource.resourceType !== resource.resourceType ||
                currentResource.resourceId !== resource.resourceId,
            ),
          );
          notify(
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionProvenanceRejectSuccess,
            ),
            'success',
          );
        }
      } catch {
        notify(
          formatMessage(
            memberDetailMessages.memberDocumentsExtractionProvenanceFeedbackError,
          ),
          'error',
        );
      } finally {
        setDecisionInFlight(null);
        setRejectingResource(null);
      }
    },
    [apiClient, formatMessage, notify, projectId, subjectId, task.task_id],
  );

  if (!clinicalResourceRefs.length && !errorMessage) {
    return null;
  }

  return (
    <Box direction="column" gap={1}>
      <Text weight="bold">
        {formatMessage(
          memberDetailMessages.memberDocumentsExtractionObservations,
        )}
      </Text>
      {errorMessage ? <Text color="negative">{errorMessage}</Text> : null}
      {resources.map(resource => {
        const approved = getResourceReviewStatus(resource) === 'approved';
        const resourceKey = `${resource.resourceType}/${resource.resourceId}`;
        const approveKey = `approved:${resourceKey}`;
        return (
          <Box key={resourceKey} direction="column" gap={0.5}>
            <Text>{getClinicalResourceDisplayName(resource)}</Text>
            <Text size="caption">
              {getClinicalResourceValueDisplay(resource)}
            </Text>
            <Text size="caption">
              {formatMessage(
                memberDetailMessages.memberDocumentsExtractionObservationResourceId,
                { id: resourceKey },
              )}
            </Text>
            <Box direction="row" gap={1} marginTop={1}>
              <Button
                variant="outlined"
                disabled={!!decisionInFlight || approved}
                onClick={() => submitFeedback(resource, 'approved')}
              >
                {decisionInFlight === approveKey
                  ? formatMessage(
                      memberDetailMessages.memberDocumentsExtractionProvenanceApproving,
                    )
                  : formatMessage(
                      memberDetailMessages.memberDocumentsExtractionProvenanceApprove,
                    )}
              </Button>
              <Button
                variant="outlined"
                color="negative"
                disabled={!!decisionInFlight}
                onClick={() => setRejectingResource(resource)}
              >
                {formatMessage(
                  memberDetailMessages.memberDocumentsExtractionProvenanceReject,
                )}
              </Button>
              <ExtractionConfidenceBadge resource={resource.resource} />
            </Box>
          </Box>
        );
      })}
      <Modal
        isOpen={!!rejectingResource}
        title={formatMessage(
          memberDetailMessages.memberDocumentsExtractionProvenanceRejectTitle,
        )}
        onDismiss={() => setRejectingResource(null)}
        actions={
          <>
            <Button
              variant="text"
              onClick={() => setRejectingResource(null)}
              disabled={!!decisionInFlight}
            >
              {formatMessage(
                memberDetailMessages.memberDocumentsExtractionProvenanceCancel,
              )}
            </Button>
            <Button
              color="negative"
              onClick={() =>
                rejectingResource &&
                submitFeedback(rejectingResource, 'rejected')
              }
              disabled={!!decisionInFlight}
            >
              {decisionInFlight ===
              (rejectingResource
                ? `rejected:${rejectingResource.resourceType}/${rejectingResource.resourceId}`
                : '')
                ? formatMessage(
                    memberDetailMessages.memberDocumentsExtractionProvenanceRejecting,
                  )
                : formatMessage(
                    memberDetailMessages.memberDocumentsExtractionProvenanceReject,
                  )}
            </Button>
          </>
        }
      >
        <Text>
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionProvenanceRejectDescription,
          )}
        </Text>
      </Modal>
    </Box>
  );
};

const DocumentExtractionTaskDetail: React.FC<{
  task: DocumentExtractionV2Invocation;
  subjectId: string;
  highlightedSourceDocumentReferenceId?: string;
}> = ({ task, subjectId, highlightedSourceDocumentReferenceId }) => {
  const { formatMessage } = useIntl();
  const output = task.output;
  const sourceLabel = getSourceLabel(
    task,
    highlightedSourceDocumentReferenceId,
    formatMessage(memberDetailMessages.memberDocumentsExtractionThisDocument),
  );
  const resourceItems = getExtractedResourceItems(task);

  return (
    <Box direction="column" gap={2} padding={3}>
      <Box direction="column" gap={1}>
        <Text weight="bold">
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionSourceDocument,
          )}
        </Text>
        <Text size="caption">{sourceLabel}</Text>
        <DocumentExtractionTaskSourcePreview task={task} />
      </Box>

      <Divider />

      <DocumentExtractionTaskClinicalResources
        task={task}
        subjectId={subjectId}
      />

      <Box direction="column" gap={1}>
        <Text weight="bold">
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionResources,
          )}
        </Text>
        {resourceItems.length ? (
          resourceItems.map(resource => (
            <Box key={resource.key} direction="column" gap={0.5}>
              <Text>{resource.label}</Text>
              {resource.details.map(detail => (
                <Text key={detail} size="caption">
                  {detail}
                </Text>
              ))}
            </Box>
          ))
        ) : (
          <Text>
            {formatMessage(
              memberDetailMessages.memberDocumentsExtractionResourcesEmpty,
            )}
          </Text>
        )}
      </Box>

      <Divider />

      <Box direction="column" gap={1}>
        <Text weight="bold">
          {formatMessage(memberDetailMessages.memberDocumentsExtractionStatus)}
        </Text>
        <Text>
          {formatMessage(
            memberDetailMessages.memberDocumentsExtractionStatusValue,
            { status: formatDocumentExtractionV2Status(task.status) },
          )}
        </Text>
        {task.status === 'failed' ? (
          <Text color="negative">{getFailureReason(task)}</Text>
        ) : null}
        <Text size="caption">Task ID: {task.task_id}</Text>
        <Text size="caption">Created: {formatDateTime(task.created)}</Text>
        <Text size="caption">
          AI summary available: {output?.summary_available ? 'Yes' : 'No'}
        </Text>
        <Text size="caption">
          Pages reviewed: {String(output?.num_pages ?? 0)}
        </Text>
        <Text size="caption">
          Candidate facts: {String(output?.candidate_fact_count ?? 0)}
        </Text>
        <Text size="caption">Errors: {String(output?.error_count ?? 0)}</Text>
        <Text size="caption">
          Artifact version: {output?.artifact_version ?? 'pending'}
        </Text>
      </Box>
    </Box>
  );
};

type DocumentExtractionV2TasksProps = {
  subjectId: string;
  highlightedSourceDocumentReferenceId?: string;
};

type DocumentExtractionV2TasksState = {
  tasks: DocumentExtractionV2Invocation[];
  selectedTaskId: string | null;
};

export const DocumentExtractionV2Tasks: React.FC<
  DocumentExtractionV2TasksProps
> = ({ subjectId, highlightedSourceDocumentReferenceId }) => {
  const { formatMessage } = useIntl();
  const apiClient = useApiClient();
  const projectId = useActiveProjectId();
  const [{ tasks, selectedTaskId }, setTaskState] =
    useState<DocumentExtractionV2TasksState>({
      tasks: [],
      selectedTaskId: null,
    });
  const [taskDetailsById, setTaskDetailsById] = useState<
    Record<string, DocumentExtractionV2Invocation>
  >({});
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const selectedTaskSummary = useMemo(
    () => tasks.find(task => task.task_id === selectedTaskId),
    [selectedTaskId, tasks],
  );
  const tasksWithSelectedDetails = useMemo(
    () =>
      tasks.map(task => {
        const detail = taskDetailsById[task.task_id];
        return detail
          ? { ...detail, ...task, output: detail.output ?? task.output }
          : task;
      }),
    [taskDetailsById, tasks],
  );

  const setSelectedTaskId = useCallback((nextSelectedTaskId: string | null) => {
    setTaskState(current => ({
      ...current,
      selectedTaskId: nextSelectedTaskId,
    }));
  }, []);

  const listColumns = useMemo(
    () => [
      {
        header: { label: 'Extraction Task' },
        cell: {
          content: (task: DocumentExtractionV2Invocation) => (
            <Box direction="column">
              <Box direction="row" justify="space-between" gap={1}>
                <Text size="table">{task.task_id}</Text>
                <Text
                  size="table"
                  color="default"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {formatDocumentExtractionV2Status(task.status)}
                </Text>
              </Box>
              <Text size="table" color="default">
                {formatDateTime(task.created)}
              </Text>
            </Box>
          ),
        },
      },
    ],
    [],
  );

  const loadTasks = useCallback(async () => {
    if (!subjectId || !projectId) {
      return;
    }
    setIsLoadingTasks(true);
    try {
      const response = await apiClient.request(
        'GET /v1/agents-v2/document-extraction-v2/tasks',
        {
          subject_id: subjectId,
          project_id: projectId,
          limit: 25,
        },
      );
      const nextTasks = response.data.tasks;
      setTaskState(current => {
        if (
          current.selectedTaskId &&
          nextTasks.some(task => task.task_id === current.selectedTaskId)
        ) {
          return {
            tasks: nextTasks,
            selectedTaskId: current.selectedTaskId,
          };
        }
        const sourceTask = highlightedSourceDocumentReferenceId
          ? nextTasks.find(task => {
              const sourceResource = getTaskSourceResource(task);
              return (
                sourceResource?.resourceType === 'DocumentReference' &&
                sourceResource.id === highlightedSourceDocumentReferenceId
              );
            })
          : undefined;
        return {
          tasks: nextTasks,
          selectedTaskId: sourceTask?.task_id ?? nextTasks[0]?.task_id ?? null,
        };
      });
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(
        formatMessage(memberDetailMessages.memberDocumentsExtractionLoadError),
      );
    } finally {
      setIsLoadingTasks(false);
    }
  }, [
    apiClient,
    formatMessage,
    highlightedSourceDocumentReferenceId,
    projectId,
    subjectId,
  ]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (!selectedTaskId) {
      return;
    }

    let isCancelled = false;

    const loadSelectedTaskDetail = async () => {
      try {
        const response = await apiClient.request(
          'GET /v1/agents-v2/document-extraction-v2/invocations/:task_id',
          {
            task_id: selectedTaskId,
          },
        );
        if (isCancelled) {
          return;
        }
        if (response.data.task_id !== selectedTaskId) {
          return;
        }
        setTaskDetailsById(current => ({
          ...current,
          [selectedTaskId]: response.data,
        }));
      } catch {
        if (!isCancelled) {
          setErrorMessage(
            formatMessage(
              memberDetailMessages.memberDocumentsExtractionLoadError,
            ),
          );
        }
      }
    };

    loadSelectedTaskDetail();

    return () => {
      isCancelled = true;
    };
  }, [apiClient, formatMessage, selectedTaskId, selectedTaskSummary?.status]);

  useEffect(() => {
    if (!tasks.some(isDocumentExtractionV2InFlight)) {
      return;
    }
    const interval = window.setInterval(loadTasks, 3000);
    return () => window.clearInterval(interval);
  }, [loadTasks, tasks]);

  return (
    <Box direction="column" gap={1} style={{ height: '100%', minHeight: 0 }}>
      {errorMessage ? <Text color="negative">{errorMessage}</Text> : null}
      <AuditMasterDetail
        rows={tasksWithSelectedDetails}
        loading={isLoadingTasks && tasks.length === 0}
        loadingVariant="dots"
        emptyMessage={formatMessage(
          memberDetailMessages.memberDocumentsExtractionNoTasks,
        )}
        noSelectionMessage={formatMessage(
          memberDetailMessages.memberDocumentsExtractionSelectTask,
        )}
        rowId={task => task.task_id}
        listColumns={listColumns}
        detailTitle={task => task.task_id}
        renderDetail={task => (
          <DocumentExtractionTaskDetail
            task={task}
            subjectId={subjectId}
            highlightedSourceDocumentReferenceId={
              highlightedSourceDocumentReferenceId
            }
          />
        )}
        selectedRowId={selectedTaskId}
        setSelectedRowId={setSelectedTaskId}
      />
    </Box>
  );
};
